import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import {
  Grid, List, ListItem, ListItemAvatar, ListItemText,
  Avatar, Tooltip, Divider, Typography, Paper
} from '@material-ui/core'

import {
  Comment as CommentIcon, History as HistoryIcon,
  Info as InfoIcon
} from '@material-ui/icons'

import moment from 'moment'

import { getSourceDescription } from '../../../utils'
import { QuarantineSubBanner } from '../tools'

import { getViolationMeta } from '../../../redux/actions/violations'
import { toggleImage } from '../../../utils/functions/toggleFunctions'

const logo = require('../../../assets/images/logo.png')

const styles = {
  navbarImageStyle: {
    maxWidth: 225,
    maxHeight: 100,
    marginLeft: '3rem'
  },
  incidentReport: {
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  textContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    transform: 'translateX(10px)'
  },
  captureImage: {
    maxHeight: '700px',
    maxWidth: '100%',
    objectFit: 'contain'
  }
}

const PrintContent = React.forwardRef((props, ref) => {
  // Determine whether the supervisor has permissions to view quarantined capture images; if so, then include the real image in the print result.
  // const userPermissions = useSelector(state => state.auth.permissions)
  // const viewPermission = userPermissions && userPermissions.length ? userPermissions.includes('violation-quarantine-viewer') : false
  // Redux state to indicate whether the supervisor has temporarily set to view the quarantine image
  const dispatch = useDispatch()

  const temporaryQuarantineView = useSelector(state => state.selection.temporaryQuarantineView)
  const userRoleSupervisors = useSelector(state => state.userListings.userRoleSupervisors)

  const actions = useSelector(state => state.violations.actions)
  const [violationMeta, setViolationMeta] = useState([])
  const [captures, setCaptures] = useState([])

  // on load, get metadata for all captures
  useMemo(() => {
    if (props.captures && props.captures.length) {
      const getMeta = async () => {
        const violationIDs = props.captures.map(capture => capture.violationID)
        const violationConfig = { violationIDs, includeComments: props.showComments, includeHistory: props.showHistory }

        const meta = await new Promise((resolve, reject) => {
          dispatch(getViolationMeta(violationConfig, resolve, reject))
        })

        if (meta && meta.violationMeta) {
          setViolationMeta(meta.violationMeta)
        }
      }
      getMeta()
    }
  }, [props.captures, props.showComments, props.showHistory, dispatch])

  // once we fetch the meta, set it appropriately for each capture
  useMemo(() => {
    if (violationMeta && props.captures && Array.isArray(props.captures) && actions && userRoleSupervisors) {
      if (violationMeta.length && props.captures.length) {
        const newCaptures = [...props.captures].reduce((arr, captureInfo) => {
          if (captureInfo && captureInfo.violationID) {
            const metaInfo = violationMeta.find((violationInfo) => violationInfo && violationInfo.violationID === captureInfo.violationID)

            if (metaInfo) {
              const mappedSupervisors = userRoleSupervisors.reduce((arr, supervisorInfo) => {
                if (supervisorInfo && supervisorInfo.roleSupervisors) { arr.push(...supervisorInfo.roleSupervisors) }
                return arr
              }, [])

              const { comments: metaComments = [], historyItems: metaHistory = [], terms: metaTerms = [] } = metaInfo

              const mappedComments = metaComments.reduce((arr, commentInfo) => {
                if (commentInfo && commentInfo.supervisorID) {
                  const commentSupervisor = mappedSupervisors.find((supervisorInfo) => supervisorInfo && supervisorInfo.supervisorID === commentInfo.supervisorID)

                  if (commentSupervisor) {
                    arr.push({ ...commentInfo, ...commentSupervisor })
                  }
                }

                return arr
              }, [])

              const mappedHistory = metaHistory.reduce((arr, historyInfo) => {
                if (historyInfo && historyInfo.supervisorID) {
                  const historySupervisor = mappedSupervisors.find((supervisorInfo) => supervisorInfo && supervisorInfo.supervisorID === historyInfo.supervisorID)
                  const historyAction = actions.find((actionInfo) => actionInfo && actionInfo.actionID === historyInfo.actionID)

                  if (historySupervisor && historyAction) {
                    const historyDetails = `${historyAction.details.toLowerCase()}`

                    arr.push({
                      ...historyInfo, ...historySupervisor, ...historyAction, historyAction: historyDetails
                    })
                  }
                }
                return arr
              }, [])

              arr.push({ ...captureInfo, comments: mappedComments, history: mappedHistory, terms: metaTerms })
            } else {
              arr.push({ ...captureInfo, comments: [], history: [], terms: [] })
            }
          }

          return arr
        }, [])

        setCaptures(newCaptures)
      } else {
        setCaptures([])
      }
    }
  }, [violationMeta, props.captures, actions, userRoleSupervisors])

  // cleanup
  useEffect(() => {
    return () => { setCaptures([]); setViolationMeta([]) }
  }, [])

  return (
    <>
      <div ref={ref}>
        <Grid item container direction='row' className='mt-3'>
          <Grid item container direction='column' xs={12}>
            <Grid item container direction='row' alignItems='center' justify='center' style={{ marginTop: '2rem' }}>
              <Grid item xs={4}>
                <img src={props.orgImageKey !== null ? `${props.orgAvatar}?${new Date().getTime()}` : logo} alt='Logo' style={styles.navbarImageStyle} />
              </Grid>
              <Grid item container xs={4} justify='center'>
                <Typography variant='h5' className='text-dark' style={styles.incidentReport}>Incident Report</Typography>
              </Grid>
              <Grid item xs={4} />
            </Grid>
          </Grid>
        </Grid>

        {captures.map((capture, index) => {
          const { redacted, quarantined } = capture
          const tempViewOnAndAllowed = Boolean(props.quarantineViewPermission && temporaryQuarantineView)

          return (
            <Paper id={index} key={index} data-violationkey={capture.violationID} elevation={2} className={`${props.classes.printPaper} page-break`}>
              <Grid item container direction='column' className='w-100 mx-auto my-auto'>
                <Grid item container direction='row' className='w-100 mx-auto'>
                  <Grid container direction='row' item xs={10} spacing={2} className='w-100 mb-2' justify='space-around' alignItems='center'>
                    <Grid item xs={5}>
                      <Typography variant='h6' className='font-weight-bold text-primary'><InfoIcon fontSize='large' /> DETAILS</Typography>
                    </Grid>

                    <Grid item xs={5}>
                      <Typography className='text-muted font-weight-bold'>Capture ID:</Typography>
                      <Typography className='text-muted'>{capture.violationID}</Typography>
                    </Grid>
                  </Grid>

                  <Grid container item direction='column' justify='center' className={props.classes.accordionDetails}>
                    <List className='w-100' dense>
                      <ListItem>
                        <Grid container direction='row' justify='center' className='w-100 mx-auto'>
                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>User:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              {
                                capture.username
                                  ? (
                                    <Typography className='text-dark'>{capture?.userDisplayName || capture.username}</Typography>)
                                  : (
                                    <Typography className='text-dark'>N/A</Typography>)
                              }
                            </Grid>
                          </Grid>

                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>All Terms Found:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              {
                                capture.terms.length
                                  ? capture.terms.map((addtlPhrase, idx) => (
                                    <Typography key={`${capture.violationID}-phrase-${idx}`} className='text-dark'>{addtlPhrase.phrase} - {addtlPhrase.severity}</Typography>
                                  ))
                                  : (
                                    <Typography className='text-dark'>{capture.phrase}</Typography>)
                              }
                            </Grid>
                          </Grid>

                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>Station:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              {
                                capture.workstationName
                                  ? (
                                    <Typography className='text-dark'>{capture.workstationName}</Typography>)
                                  : (
                                    <Typography className='text-dark'>N/A</Typography>)
                              }
                            </Grid>
                          </Grid>
                        </Grid>
                      </ListItem>

                      <Divider variant='middle' className={props.classes.divider} />

                      <ListItem>
                        <Grid container direction='row' justify='center' spacing={2} className='w-100 mx-auto'>
                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>Source:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              {
                                getSourceDescription(capture.source)
                                  ? (
                                    <Typography className='text-dark'>{getSourceDescription(capture.source)}</Typography>)
                                  : (
                                    <Typography className='text-dark'>N/A</Typography>)
                              }
                            </Grid>
                          </Grid>

                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>Application:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              {
                                capture.applicationName
                                  ? (
                                    <Typography className='text-dark'>{capture.applicationName}</Typography>)
                                  : (
                                    <Typography className='text-dark'>N/A</Typography>)
                              }
                            </Grid>
                          </Grid>

                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>Category:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              {
                                capture.categoryName
                                  ? (
                                    <Typography className='text-dark'>{capture.categoryName}</Typography>)
                                  : (
                                    <Typography className='text-dark'>N/A</Typography>)
                              }
                            </Grid>
                          </Grid>
                        </Grid>
                      </ListItem>

                      <Divider variant='middle' className={props.classes.divider} />

                      <ListItem>
                        <Grid container direction='row' justify='center' spacing={2} className='w-100 mx-auto'>
                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>Machine Time:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              {
                                capture.reportedAt
                                  ? (
                                    <Typography className='text-dark'>{moment.unix(capture.reportedAt / 1000).format('MM/DD/YYYY LT')}</Typography>)
                                  : (
                                    <Typography className='text-dark'>N/A</Typography>)
                              }
                            </Grid>
                          </Grid>

                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>IP Addr:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              {
                                capture.station_ip
                                  ? (
                                    <Typography className='text-dark'>{capture.station_ip}</Typography>)
                                  : (
                                    <Typography className='text-dark'>N/A</Typography>)
                              }
                            </Grid>
                          </Grid>

                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>Location:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark'>{props.renderUnit(capture)}</Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </ListItem>

                      <Divider variant='middle' className={props.classes.divider} />

                      <ListItem>
                        <Grid container direction='row' justify='flex-start' spacing={2} className='w-100 mx-auto'>
                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>Console Time:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              <Typography variant='body1' className='text-dark'>{moment.unix(capture.receivedAt / 1000).format('MM/DD/YYYY LT')}</Typography>
                            </Grid>
                          </Grid>

                          <Grid item container direction='column' xs={4} justify='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography className='text-dark font-weight-bold'>Received Time:</Typography>
                            </Grid>
                            <Grid item container direction='row' justify='center'>
                              <Typography variant='body1' className='text-dark'>{moment.unix(capture.receivedAt / 1000).format('MM/DD/YYYY LT')}</Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>

                <Grid item container justify='center'>
                  {toggleImage(capture, tempViewOnAndAllowed, null, styles.captureImage)}
                  {quarantined && !tempViewOnAndAllowed && <QuarantineSubBanner redacted={redacted} fontSize={16} />}
                </Grid>

                <Grid item container>
                  {
                    props.showComments === true && capture.comments && capture.comments.length &&
                      <Grid item container direction='row' className='w-100 mx-auto mt-3'>
                        <Typography variant='h6' className='font-weight-bold text-primary' style={{ marginLeft: '2rem', marginBottom: '1rem' }}><CommentIcon fontSize='large' /> COMMENTS</Typography>
                        <Grid container item direction='column' justify='center' className={props.classes.accordionDetails}>
                          <List dense className={props.classes.contentList}>
                            {capture.comments.map((comment, index) => {
                              const date = new Date(comment.timestamp)
                              const actualDate = `${(date.getMonth() + 1)}/${date.getDate()}/${date.getFullYear()}`
                              const actualTime = moment(date).format('LTS')
                              return (
                                <div key={`${capture.violationID}-comment-${index}`}>
                                  {
                                    capture.violationID === comment.violationID &&
                                      <>
                                        <ListItem>
                                          <ListItemAvatar>
                                            <Tooltip title={`${comment.firstName} ${comment.lastName}`} placement='top'>
                                              <Avatar className={props.classes.commentAvatar} src={comment.avatarPath}>
                                                {comment.avatarPath ? (<></>) : comment.firstName.substring(0, 1)}{comment.lastName.substring(0, 1)}
                                              </Avatar>
                                            </Tooltip>
                                          </ListItemAvatar>
                                          <ListItemText
                                            primary={
                                              <Grid container>
                                                <Grid item xs={9} container direction='row' spacing={2}>
                                                  <Grid item xs={8} container alignContent='center'>
                                                    <Typography variant='body1' className='font-weight-bold text-wrap w-100'>{comment.firstName} {comment.lastName}</Typography>
                                                  </Grid>
                                                  <Grid item container xs={4} alignContent='center'>
                                                    <Typography variant='caption' className='text-muted'>
                                                      {actualTime}
                                                    </Typography>
                                                  </Grid>
                                                </Grid>
                                                <Grid item xs={3} container direction='row' justify='flex-end' alignContent='center'>
                                                  <Typography variant='caption' className='text-muted'>
                                                    {actualDate}
                                                  </Typography>
                                                </Grid>
                                              </Grid>
                                            }
                                            secondary={
                                              <Grid container>
                                                <Grid container direction='row'>
                                                  <Typography component='span' variant='body2' className='text-wrap w-100'>{comment.comment}</Typography>
                                                </Grid>
                                              </Grid>
                                            }
                                            className='text-dark'
                                            disableTypography
                                          />
                                        </ListItem>
                                        <Divider variant='inset' component='li' className={props.classes.divider} />
                                      </>
                                  }
                                </div>
                              )
                            })}
                          </List>
                        </Grid>
                      </Grid>
                  }

                  {
                    props.showHistory === true && capture.history && capture.history.length &&
                      <Grid item container direction='row' className='w-100 mx-auto mt-3'>
                        <Typography variant='h6' className='font-weight-bold text-primary' style={{ marginLeft: '2rem', marginBottom: '1rem' }}><HistoryIcon fontSize='large' /> HISTORY</Typography>
                        <Grid container item direction='column' justify='center' className={props.classes.accordionDetails}>
                          <List dense className={props.classes.contentList}>
                            {capture.history.map((history, index) => {
                              return (
                                <div key={`${capture.violationID}-history-${index}`}>
                                  {
                                    capture.violationID === history.violationID &&

                                      <>
                                        <ListItem>
                                          <ListItemAvatar>
                                            <Tooltip title={`${history.firstName} ${history.lastName}`} placement='top'>
                                              <Avatar className={props.classes.commentAvatar} src={history.avatarPath}>
                                                {history.firstName.substring(0, 1)}{history.lastName.substring(0, 1)}
                                              </Avatar>
                                            </Tooltip>
                                          </ListItemAvatar>
                                          <ListItemText
                                            className='text-dark'
                                            disableTypography
                                            primary={
                                              <Grid container>
                                                <Grid item xs={9} container direction='row'>
                                                  <Typography variant='body1' className='font-weight-bold text-wrap w-100'>{history.firstName} {history.lastName}</Typography>
                                                </Grid>
                                              </Grid>
                                            }
                                            secondary={
                                              <Typography component='span' variant='body2' className='text-wrap w-100'>
                                                <span className='text-capitalize'>{history.historyAction}</span> on
                                                <span className='text-primary font-italic'> {moment.unix(history.timestamp).format('MMMM Do YYYY, h:mma')}</span>
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                        <Divider variant='middle' className={props.classes.divider} />
                                      </>
                                  }
                                </div>
                              )
                            })}
                          </List>
                        </Grid>
                      </Grid>
                  }

                </Grid>
              </Grid>
            </Paper>
          )
        }
        )}
      </div>
    </>
  )
})

export default PrintContent
